import { makeAutoObservable } from "mobx";

import { SummaryProviderType } from "../../@api";

import { IFileUploadErrors, ILanguageOption, IProgressedFileResult, ISummaryUploadFile } from "../models/interfaces";

interface ITextSummaryState {
  textToSummarize: string;
  textSummarizationResult: IProgressedFileResult | undefined;
  isTextProcessing: boolean;
  maxOutputSentences: number;
  selectedOutputLanguage: ILanguageOption | undefined;
  selectedSummarizationMethod: SummaryProviderType;
}

export class SummaryStore {
  private _errors: IFileUploadErrors = {};

  private _textSummaryState: ITextSummaryState = {
    textToSummarize: "",
    textSummarizationResult: undefined,
    isTextProcessing: false,
    maxOutputSentences: 3,
    selectedOutputLanguage: undefined,
    selectedSummarizationMethod: SummaryProviderType.AzureSummarizer,
  };

  private _uploadedFiles: ISummaryUploadFile[] = [];
  private _currentProgressingFileIds: string[] = [];

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  public get errors(): IFileUploadErrors {
    return this._errors;
  }

  public set errors(value: IFileUploadErrors) {
    this._errors = value;
  }

  // Text summarization state
  public get textSummaryState(): ITextSummaryState {
    return this._textSummaryState;
  }
  public setTextToSummarize(value: string) {
    this._textSummaryState.textToSummarize = value;
  }

  public setTextSummarizationResult(value: IProgressedFileResult | undefined) {
    this._textSummaryState.textSummarizationResult = value;
  }

  public setTextProcessing(value: boolean) {
    this._textSummaryState.isTextProcessing = value;
  }

  public setMaxOutputSentences(value: number) {
    this._textSummaryState.maxOutputSentences = value;
  }

  public setSelectedOutputLanguage(value: ILanguageOption | undefined) {
    this._textSummaryState.selectedOutputLanguage = value;
  }

  public setSelectedSummarizationMethod(value: SummaryProviderType) {
    this._textSummaryState.selectedSummarizationMethod = value;
  }

  // File upload state
  public get uploadedFiles(): ISummaryUploadFile[] {
    return this._uploadedFiles;
  }

  public setUploadedFiles(value: ISummaryUploadFile[]) {
    this._uploadedFiles = value;
  }

  public get isAnyFileProgressing(): boolean {
    return this._currentProgressingFileIds.length > 0;
  }

  public getUploadedFileById(fileId: string | undefined): ISummaryUploadFile | undefined {
    return this._uploadedFiles.find(f => f.id === fileId);
  }

  public setUploadedFileMaxOutputSentences(fileId: string, value: number): void {
    const file = this.getUploadedFileById(fileId);
    if (!file) {
      return;
    }

    file.maxOutputSentences = value;
  }

  public setUploadedFileOutputLanguage(fileId: string, value: ILanguageOption | undefined): void {
    const file = this.getUploadedFileById(fileId);
    if (!file) {
      return;
    }

    file.outputLanguage = value;
  }

  public setUploadedFileSummarizationMethod(fileId: string, value: SummaryProviderType): void {
    const file = this.getUploadedFileById(fileId);
    if (!file) {
      return;
    }

    file.summarizationMethod = value;
  }

  public setUploadedFileResult(fileId: string, value: IProgressedFileResult | undefined): void {
    const file = this.getUploadedFileById(fileId);
    if (!file) {
      return;
    }

    file.result = value;
  }

  public addProgressingFile(fileId: string): void {
    if (this.containsProgressingFile(fileId)) {
      return;
    }

    this._currentProgressingFileIds = [...this._currentProgressingFileIds, fileId];
  }

  public removeProgressingFile(fileId: string): void {
    this._currentProgressingFileIds = this._currentProgressingFileIds.filter(
      progressingFileId => progressingFileId !== fileId,
    );
  }

  public containsProgressingFile(fileId: string): boolean {
    return this._currentProgressingFileIds.some(progressingFileId => progressingFileId === fileId);
  }
}
